import { useQuery } from "@tanstack/react-query";
import { toastError, toastSuccess } from "helpers";
import { createLogger } from "logging";
import { CreateRunRequestModel } from "models/CreateRunRequestModel";
import { CreateRunResultModel } from "models/CreateRunResultModel";
import { useApi, usePermissions } from "providers";
import { useMeeting } from "providers";
import { useTranslations } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";

import { EdecidePanel } from "./components/EdecidePanel";
import { EdecidePanelContext } from "./EdecidePanelContext";

const logger = createLogger("EdecidePanelProvider");

export const EdecidePanelProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { handleHttpError } = usePermissions();
  const i18n = useTranslations();
  const { edecideApi } = useApi();
  const { meetingId } = useMeeting();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [runTitle, setRunTitle] = useState("");
  const [runPreview, setRunPreview] = useState<CreateRunRequestModel | null>(null);
  const [runResult, setRunResult] = useState<CreateRunResultModel | null>(null);

  // Load preview when panel opens with document
  const queryResult = useQuery({
    queryKey: [`Meeting|${meetingId}|EdecidePreview`],
    queryFn: async () => {
      if (!meetingId) return;

      let data: CreateRunRequestModel;
      try {
        data = await edecideApi.getPreview(meetingId);
        setRunTitle(data.title || "");
        setRunPreview(data);
      } catch (error) {
        toastError("Failed to load preview", error);
        throw error;
      }

      logger.trace("Loaded", data);
      return data;
    },
    enabled: false, // Don't run on mount
  });
  const isLoading = isOpen && queryResult.isFetching;
  const { refetch, error } = queryResult;

  useEffect(() => {
    handleHttpError(error);
  }, [error, handleHttpError]);

  const resetForm = useCallback(() => {
    setRunTitle("");
    setRunPreview(null);
    setRunResult(null);
  }, []);

  const openEdecidePanel = useCallback(() => {
    logger.debug("openEdecidePanel(): ...");
    setIsOpen(true);
    resetForm();
    refetch();
  }, [refetch, resetForm]);

  const closeEdecidePanel = useCallback(() => {
    logger.debug("closeEdecidePanel()");
    setIsOpen(false);
    setIsSubmitting(false);
    resetForm();
  }, [resetForm]);

  const handleSetRunTitle = useCallback((title: string) => {
    logger.debug("setRunTitle()", title);
    setRunTitle(title);
  }, []);

  const submitEdecidePanel = useCallback(async () => {
    if (!meetingId) throw new Error("No meeting ID");

    logger.debug("Submitting run:", runTitle);
    setIsSubmitting(true);

    const data = {
      title: runTitle,
    };

    logger.debug("Creating run:", data);
    try {
      const result = await edecideApi.createRun(meetingId, data);
      setRunResult(result);
      if (result.success) {
        toastSuccess(i18n.EdecidePanel_CreateRun_Success);
      }
    } catch (error) {
      toastError(i18n.EdecidePanel_CreateRun_Failure, error);
    } finally {
      setIsSubmitting(false);
    }
  }, [i18n, runTitle, edecideApi, meetingId]);

  const isSubmitDisabled = useMemo(() => {
    const existingRuns = runPreview?.existingRuns?.length || 0;
    return !runPreview || isSubmitting || existingRuns > 0;
  }, [runPreview, isSubmitting]);

  const isPreview = useMemo(() => {
    return !isLoading && runPreview != null && !isSubmitting && runResult == null;
  }, [isLoading, runPreview, isSubmitting, runResult]);

  const isResult = useMemo(() => {
    return !isLoading && runResult != null;
  }, [isLoading, runResult]);

  const contextValue = useMemo(
    () => ({
      error,
      isOpen,
      isLoading,
      isPreview,
      isResult,
      isSubmitting,
      isSubmitDisabled,
      runTitle,
      runPreview,
      runResult,
      openEdecidePanel,
      closeEdecidePanel,
      setRunTitle: handleSetRunTitle,
      submitEdecidePanel,
    }),
    [
      error,
      isOpen,
      isLoading,
      isSubmitting,
      isPreview,
      isResult,
      isSubmitDisabled,
      runTitle,
      runPreview,
      runResult,
      openEdecidePanel,
      closeEdecidePanel,
      handleSetRunTitle,
      submitEdecidePanel,
    ]
  );

  return (
    <EdecidePanelContext.Provider value={contextValue}>
      {children}
      <EdecidePanel />
    </EdecidePanelContext.Provider>
  );
};
